import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Button, message, Spin } from 'antd';
import {
  DashboardOutlined,
  DesktopOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  UnorderedListOutlined,
  CalendarOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { themeColors } from '../config/theme';

const { Header, Sider, Content, Footer } = Layout;

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKey, setSelectedKey] = useState(['dashboard']);
  const [loading, setLoading] = useState(true);
  const user = useAuthStore((state) => state.user);
  const fetchUser = useAuthStore((state) => state.fetchUser);
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && !user) {
      fetchUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user, fetchUser]);

  useEffect(() => {
    const path = location.pathname.replace('/dashboard/', '').split('/')[0] || 'dashboard';
    setSelectedKey([path]);
  }, [location.pathname]);

  const isSubAdmin = user?.role === 'sub_admin';

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: 'screens',
      icon: <DesktopOutlined />,
      label: 'Screens',
      onClick: () => navigate('/dashboard/screens'),
    },
    ...(isSubAdmin
      ? []
      : [
          {
            key: 'clients',
            icon: <TeamOutlined />,
            label: 'Clients',
            onClick: () => navigate('/dashboard/clients'),
          },
        ]),
    {
      key: 'videos',
      icon: <VideoCameraOutlined />,
      label: 'Videos',
      onClick: () => navigate('/dashboard/videos'),
    },
    {
      key: 'playlists',
      icon: <UnorderedListOutlined />,
      label: 'Playlists',
      onClick: () => navigate('/dashboard/playlists'),
    },
    {
      key: 'schedules',
      icon: <CalendarOutlined />,
      label: 'Schedules',
      onClick: () => navigate('/dashboard/schedules'),
    },
    {
      key: 'reports',
      icon: <FileTextOutlined />,
      label: 'Reports',
      onClick: () => navigate('/dashboard/reports'),
    },
    {
      key: 'playback-report',
      icon: <LineChartOutlined />,
      label: 'Playback Report',
      onClick: () => navigate('/dashboard/playback-report'),
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const handleUserMenuClick = async (e) => {
    if (e.key === 'logout') {
      await logout();
      message.success('Logged out successfully');
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: themeColors.bg,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: themeColors.bg }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          backgroundColor: themeColors.sidebarBg,
          borderRight: `1px solid ${themeColors.sidebarBorder}`,
        }}
      >
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            borderBottom: `1px solid ${themeColors.sidebarBorder}`,
            marginBottom: '20px',
          }}
        >
          <img
            src="/cloudnet-logo.png"
            alt="CloudNet"
            style={{
              height: collapsed ? '50px' : '80px',
              marginBottom: '10px',
              objectFit: 'contain',
              transition: 'height 0.3s ease',
            }}
          />
        </div>

        <Menu
          theme="light"
          selectedKeys={selectedKey}
          mode="inline"
          items={menuItems}
          style={{
            backgroundColor: themeColors.sidebarBg,
            borderRight: 'none',
          }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            backgroundColor: themeColors.sidebarBg,
            borderBottom: `1px solid ${themeColors.sidebarBorder}`,
            padding: '0 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ color: themeColors.primary, fontSize: '18px' }}
          />

          <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }}>
            <Button
              type="text"
              icon={<UserOutlined />}
              style={{
                color: themeColors.primary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {user?.email}
            </Button>
          </Dropdown>
        </Header>

        <Content
          style={{
            padding: '24px',
            backgroundColor: themeColors.bg,
            overflow: 'auto',
          }}
        >
          {children}
        </Content>

        <Footer
          style={{
            textAlign: 'center',
            backgroundColor: themeColors.bg,
            color: themeColors.textSecondary,
            padding: '12px 24px',
          }}
        >
          Designed & Developed by CloudNet Softwares | +66-948712350
        </Footer>
      </Layout>
    </Layout>
  );
}
